// Login Page (NEW DESIGN — visual revamp only). Logic is a verbatim copy of
// Login.js — every condition, every effect, every navigation is preserved. The
// only differences are:
//   1. Imports LoginNewWrapper instead of LoginWrapper.
//   2. Uses the Figma split-screen layout (purple left panel + white right card).
//      Banner col is visually on the LEFT and form col on the RIGHT — achieved
//      via antd `order` prop, while keeping the form col FIRST in the DOM so
//      the LoginStyle :first-child mobile rule still gives it flex:1 / full-width
//      on small screens.
//   3. Internal navigations point at the "-new" sibling routes so a tester who
//      enters this flow stays inside the new design end-to-end.
//
// Do NOT touch any condition / rendering rule here without also touching the
// original Login.js — they must stay in lockstep.
import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Input, notification } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { ThemeForm, ThemeButton } from '../Styles/Theme';
import {
	loginApi,
	loginReset,
	verifyOtpApi,
	resendOtpApi,
} from '../../store/actions/loginAction';
import { setPermissions } from '../../store/actions/permissionsAction';
import { handleInputChange } from '../../utils/formFunctions';
import { STORAGE_KEYS } from '../../utils/constants';
import { checkModulePermissions, getFirstAvailableRoute, isLogin } from '../../utils/helpers';
import { landOnToplineOr } from '../../store/actions/projectAction';
import { LoginNewWrapper } from './LoginNewStyle';
import LoginLeftPanel from './LoginLeftPanel';

const LoginNew = (props) => {
	const { login, loginReset, loginState, setPermissions, verifyOtp, resendOtp } = props;
	const [form] = ThemeForm.useForm();
	const [formData, setFormData] = useState({});
	const [loading, setLoading] = useState(false);
	const [step, setStep] = useState('email'); // 'email' | 'password' | 'otp'
	const [otpSessionToken, setOtpSessionToken] = useState(null);
	const [otpMaskedEmail, setOtpMaskedEmail] = useState(null);
	const [otpSecondsLeft, setOtpSecondsLeft] = useState(0);
	const navigate = useNavigate();

	// OTP countdown — tick every second while on the OTP step.
	useEffect(() => {
		if (step !== 'otp' || otpSecondsLeft <= 0) return undefined;
		const id = setInterval(() => {
			setOtpSecondsLeft((s) => Math.max(0, s - 1));
		}, 1000);
		return () => clearInterval(id);
	}, [step, otpSecondsLeft]);

	// If the user is already signed in, don't show the login form — send them to
	// the dashboard (or their first accessible route if they can't see the dashboard).
	useEffect(() => {
		if (isLogin()) {
			// Topline first when there is a summary on it to see (client
			// 2026-09-28), ahead of the per-org chat redirect below.
			const redirectChatId = loginState.data?.redirect_chat_id;
			landOnToplineOr(() => navigate('/topline', { replace: true }), () => {
				// Per-org login redirect: backend sets redirect_chat_id when the
				// user's org has chat_redirection_enabled. Take that path before
				// the normal landing-page fallback so the user lands directly in
				// their fresh "New Insights" chat.
				if (redirectChatId) {
					navigate(`/insight-engine/${redirectChatId}`);
					return;
				}
				const dashboardPermission = checkModulePermissions('dashboard');
				if (dashboardPermission.authorized) {
					navigate('/', { replace: true });
				} else {
					const first = getFirstAvailableRoute();
					navigate(first ? first.path : '/', { replace: true });
				}
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		// Step 1 response: backend tells us which mode
		if (loginState.apiState === 'mfa_required') {
			setLoading(false);
			if (loginState.mfaSessionToken) {
				localStorage.setItem('mfaSessionToken', loginState.mfaSessionToken);
			}
			if (loginState.maskedEmail) {
				localStorage.setItem('mfaMaskedEmail', loginState.maskedEmail);
			}
			if (loginState.mfaExpiryMinutes) {
				localStorage.setItem('mfaExpiryMinutes', String(loginState.mfaExpiryMinutes));
			}
			loginReset();
			navigate('/auth/sign-in/check');
			return;
		}

		if (loginState.apiState === 'password_required') {
			setLoading(false);
			setStep('password');
			loginReset();
			return;
		}

		if (loginState.apiState === 'otp_required') {
			setLoading(false);
			setOtpSessionToken(loginState.mfaSessionToken || null);
			setOtpMaskedEmail(loginState.maskedEmail || null);
			const minutes = loginState.mfaExpiryMinutes || 3;
			setOtpSecondsLeft(Math.max(60, minutes * 60));
			setStep('otp');
			loginReset();
			return;
		}

		if (loginState.apiState === 'reset_password_required') {
			setLoading(false);
			const data = loginState.data || {};
			if (data.sessionToken) {
				localStorage.setItem(STORAGE_KEYS.SESSION_TOKEN, data.sessionToken);
			}
			if (data.user) {
				localStorage.setItem(STORAGE_KEYS.PENDING_USER, JSON.stringify(data.user));
			}
			loginReset();
			navigate('/auth/np2w8q');
			return;
		}

		if (loginState.apiState === 'success') {
			setLoading(false);
			if (loginState.data) {
				localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, loginState.data.accessToken);
				localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, loginState.data.refreshToken);
				localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(loginState.data.user));
				if (loginState.data.expiresAt) {
					localStorage.setItem('expiresAt', loginState.data.expiresAt);
				}
				if (loginState.data.permissions) {
					localStorage.setItem(STORAGE_KEYS.PERMISSIONS, JSON.stringify(loginState.data.permissions));
					setPermissions(loginState.data.permissions);
				}
				if (loginState.data.appVersion) {
					localStorage.setItem('app_version', loginState.data.appVersion);
				}
			}
			loginReset();
			// Topline first when there is a summary on it to see (client
			// 2026-09-28), ahead of the per-org chat redirect below.
			const redirectChatId = loginState.data?.redirect_chat_id;
			landOnToplineOr(() => navigate('/topline'), () => {
				// Per-org login redirect: backend sets redirect_chat_id when the
				// user's org has chat_redirection_enabled. Take that path before
				// the normal landing-page fallback so the user lands directly in
				// their fresh "New Insights" chat.
				if (redirectChatId) {
					navigate(`/insight-engine/${redirectChatId}`);
					return;
				}
				const dashboardPermission = checkModulePermissions('dashboard');
				if (dashboardPermission.authorized) {
					navigate('/');
				} else {
					const firstAvailableRoute = getFirstAvailableRoute();
					navigate(firstAvailableRoute ? firstAvailableRoute.path : '/');
				}
			});
		} else if (loginState.apiState === 'error') {
			setLoading(false);
			const errorCode = loginState.data?.data?.code;
			if (errorCode === 'ACCOUNT_PENDING') {
				notification.warning({
					message: 'Please use the activation link in your invitation email.',
				});
			} else if (errorCode === 'ACCOUNT_SUSPENDED') {
				notification.error({
					message: 'Account suspended. Contact your administrator.',
				});
			} else {
				notification.error({ message: loginState.message || 'Login failed' });
			}
			loginReset();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loginState]);

	const handleSubmit = () => {
		setLoading(true);
		if (step === 'email') {
			login({ email: formData.email });
		} else if (step === 'password') {
			login({ email: formData.email, password: formData.password });
		} else if (step === 'otp') {
			verifyOtp({ email: formData.email, mfaSessionToken: otpSessionToken, otp: formData.otp });
		}
	};

	const handleBackToEmail = () => {
		setStep('email');
		form.setFieldsValue({ password: '', otp: '' });
		setFormData({ ...formData, password: '', otp: '' });
		setOtpSessionToken(null);
		setOtpMaskedEmail(null);
		setOtpSecondsLeft(0);
	};

	const handleResendOtp = async () => {
		if (!otpSessionToken) return;
		const res = await resendOtp({ email: formData.email, mfaSessionToken: otpSessionToken });
		if (res?.status === 1) {
			if (res?.data?.mfaSessionToken) setOtpSessionToken(res.data.mfaSessionToken);
			notification.success({ message: 'Verification code resent' });
		} else {
			notification.error({ message: res?.message || 'Could not resend code' });
		}
	};

	return (
		<LoginNewWrapper>
			<Row gutter={[20, 16]}>
				{/* Form col — FIRST in DOM (so LoginStyle's `:first-child` mobile rule
				    still applies and form gets flex:1 + full width on small screens),
				    but visually on the RIGHT on md+ via antd `order` prop. */}
				<Col
					xs={{ span: 24, order: 1 }}
					sm={{ span: 24, order: 1 }}
					md={{ span: 12, order: 2 }}
					lg={{ span: 14, order: 2 }}
					xl={{ span: 14, order: 2 }}
				>
					<div className='loginRightCard'>
						<div className='loginLogo'>
							<img src='/images/login-new/crowdvox-logo.svg' alt='CrowdVox' />
						</div>
						<div className='loginContainer'>
							<h1>{step === 'password' ? 'Welcome back to CrowdVox' : 'Sign in to CrowdVox'}</h1>
							{step === 'email' && (
								<p>Please enter your details to sign in your account</p>
							)}
							{step === 'password' && (
								<p>
									<span className='emailLinkGroup'>
										<strong>{formData.email}</strong>.{' '}
										<a onClick={handleBackToEmail}>
											<ArrowLeftOutlined style={{ fontSize: 11 }} /> Change email
										</a>
									</span>
								</p>
							)}
							{step === 'otp' && (
								<p>
									We've sent a 6-digit verification code to{' '}
									<span className='emailLinkGroup'>
										<strong>{otpMaskedEmail || formData.email}</strong>.{' '}
										<a onClick={handleBackToEmail}>
											<ArrowLeftOutlined style={{ fontSize: 11 }} /> Start over
										</a>
									</span>
									{otpSecondsLeft > 0 && (
										<>
											{' '}· <span style={{ color: otpSecondsLeft < 30 ? '#D4380D' : '#6D6A67' }}>
												Code valid for {Math.floor(otpSecondsLeft / 60)}:{String(otpSecondsLeft % 60).padStart(2, '0')}
											</span>
										</>
									)}
									{otpSecondsLeft === 0 && (
										<>
											{' '}· <span style={{ color: '#D4380D' }}>Code expired - please start over</span>
										</>
									)}
								</p>
							)}
							<ThemeForm
								form={form}
								name='loginFormNew'
								layout='vertical'
								onFinish={handleSubmit}
								className='loginFormV2'
							>
								<ThemeForm.Item
									name='email'
									label='Email'
									rules={[
										{ required: true, message: 'Please enter email address' },
										{ type: 'email', message: 'Please enter a valid email' },
									]}
								>
									<Input
										size='large'
										name='email'
										type='email'
										autoComplete='username'
										placeholder='johndoe@mail.com'
										prefix={<img src='/images/login-new/icon-envelope.svg' alt='' />}
										disabled={step === 'password' || step === 'otp'}
										onChange={(e) => handleInputChange(e, formData, setFormData)}
									/>
								</ThemeForm.Item>

								{step === 'password' && (
									<ThemeForm.Item
										name='password'
										label='Password'
										rules={[{ required: true, message: 'Please enter password' }]}
									>
										<Input.Password
											name='password'
											size='large'
											autoComplete='current-password'
											placeholder='minimum 8 character'
											prefix={<img src='/images/login-new/icon-lock.svg' alt='' />}
											autoFocus
											onChange={(e) => handleInputChange(e, formData, setFormData)}
										/>
									</ThemeForm.Item>
								)}

								{step === 'password' && (
									<p style={{ textAlign: 'right', marginTop: -8, marginBottom: 12 }}>
										<a onClick={() => navigate('/auth/fp7r4q')}>Forgot password?</a>
									</p>
								)}

								{step === 'otp' && (
									<ThemeForm.Item
										name='otp'
										label='Verification Code'
										rules={[
											{ required: true, message: 'Please enter the 6-digit code' },
											{ pattern: /^\d{6}$/, message: 'Code must be 6 digits' },
										]}
									>
										<Input
											name='otp'
											size='large'
											placeholder='6-digit code'
											maxLength={6}
											autoFocus
											onChange={(e) => handleInputChange(e, formData, setFormData)}
										/>
									</ThemeForm.Item>
								)}

								{step === 'otp' && (
									<div className='loginResendRow'>
										<a onClick={handleResendOtp}>Resend code</a>
									</div>
								)}

								<ThemeForm.Item style={{ marginBottom: 0, marginTop: 8 }}>
									{loading ? (
										<ThemeButton size='large' loading className='loginBtn'>
											{step === 'email' ? 'Checking...' : step === 'otp' ? 'Verifying...' : 'Signing In...'}
										</ThemeButton>
									) : (
										<ThemeButton size='large' className='loginBtn' htmlType='submit'>
											{step === 'email' ? 'Continue' : step === 'otp' ? 'Verify' : 'Sign in'}
										</ThemeButton>
									)}
								</ThemeForm.Item>

								{step === 'email' && (
									<p style={{ textAlign: 'center', marginTop: 14 }}>
										<a onClick={() => navigate('/auth/fp7r4q')}>Forgot password?</a>
									</p>
								)}
							</ThemeForm>
						</div>
						<div className='loginFooter'>
							<span>@{new Date().getFullYear()} CrowdVox Inc.</span>
						</div>
					</div>
				</Col>

				{/* Banner col — SECOND in DOM, visually on the LEFT at md+ via order:1.
				    Hidden on mobile via .loginLeftCard { display:none } (mirror of
				    LoginStyle's .loginBanner mobile rule). */}
				<Col
					xs={{ span: 0, order: 2 }}
					sm={{ span: 0, order: 2 }}
					md={{ span: 12, order: 1 }}
					lg={{ span: 10, order: 1 }}
					xl={{ span: 10, order: 1 }}
				>
					<LoginLeftPanel />
				</Col>
			</Row>
		</LoginNewWrapper>
	);
};

const mapStateToProps = (state) => ({
	loginState: state.login,
});

const mapDispatchToProps = (dispatch) => ({
	login: (params) => dispatch(loginApi(params)),
	loginReset: () => dispatch(loginReset()),
	setPermissions: (params) => dispatch(setPermissions(params)),
	verifyOtp: (params) => dispatch(verifyOtpApi(params)),
	resendOtp: (params) => dispatch(resendOtpApi(params)),
});

export default connect(mapStateToProps, mapDispatchToProps)(LoginNew);
